import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import './MobileAvatar.css';

interface MobileAvatarProps {
  isSpeaking: boolean;
}

// Simplified robot head for mobile view
function RobotHead({ isSpeaking }: { isSpeaking: boolean }) {
  const headRef = useRef<THREE.Group>(null);
  const leftEyeRef = useRef<THREE.Mesh>(null);
  const rightEyeRef = useRef<THREE.Mesh>(null);
  const mouthRef = useRef<THREE.Mesh>(null);

  const blinkTimer = useRef(0);
  const speakTimer = useRef(0);

  useFrame((state, delta) => {
    if (!headRef.current) return;

    // Gentle head rotation
    if (!isSpeaking) {
      const time = state.clock.elapsedTime;
      headRef.current.rotation.y = Math.sin(time * 0.4) * 0.15;
      headRef.current.rotation.x = Math.sin(time * 0.25) * 0.05;
    }

    // Blinking
    blinkTimer.current += delta;
    if (blinkTimer.current > 3) {
      blinkTimer.current = 0;
    }
    const blinkProgress =
      blinkTimer.current < 0.12
        ? blinkTimer.current / 0.12
        : blinkTimer.current < 0.2
          ? 1 - (blinkTimer.current - 0.12) / 0.08
          : 0;

    if (leftEyeRef.current && rightEyeRef.current) {
      const blinkScale = 1 - blinkProgress;
      leftEyeRef.current.scale.y = blinkScale;
      rightEyeRef.current.scale.y = blinkScale;
    }

    // Speaking animation
    if (isSpeaking && mouthRef.current && headRef.current) {
      speakTimer.current += delta * 12;
      const mouthOpen = Math.sin(speakTimer.current) * 0.5 + 0.5;
      const mouthVariation = Math.sin(speakTimer.current * 1.8) * 0.3;

      const targetScaleY = 0.5 + mouthOpen * 0.8 + mouthVariation * 0.2;
      const targetScaleX = 1.2 - mouthOpen * 0.3;

      mouthRef.current.scale.y = THREE.MathUtils.lerp(mouthRef.current.scale.y, targetScaleY, 0.3);
      mouthRef.current.scale.x = THREE.MathUtils.lerp(mouthRef.current.scale.x, targetScaleX, 0.3);

      // Head movement while speaking
      const time = state.clock.elapsedTime;
      headRef.current.position.y = Math.sin(time * 3) * 0.03;
      headRef.current.rotation.x = Math.sin(time * 2.5) * 0.06;
      headRef.current.rotation.y = Math.sin(time * 0.7) * 0.1;
    } else if (mouthRef.current && headRef.current) {
      mouthRef.current.scale.y = THREE.MathUtils.lerp(mouthRef.current.scale.y, 0.5, 0.2);
      mouthRef.current.scale.x = THREE.MathUtils.lerp(mouthRef.current.scale.x, 1.2, 0.2);
      headRef.current.position.y = THREE.MathUtils.lerp(headRef.current.position.y, 0, 0.1);
    }
  });

  return (
    <group ref={headRef} scale={0.8}>
      {/* Head */}
      <mesh>
        <boxGeometry args={[1.2, 1.1, 1]} />
        <meshStandardMaterial color="#fef3c7" roughness={0.1} metalness={0.7} />
      </mesh>

      {/* Face screen border */}
      <mesh position={[0, 0, 0.51]}>
        <boxGeometry args={[1.05, 0.95, 0.02]} />
        <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.9} />
      </mesh>

      {/* Face screen */}
      <mesh position={[0, 0, 0.52]}>
        <boxGeometry args={[1, 0.9, 0.03]} />
        <meshStandardMaterial
          color="#dbeafe"
          roughness={0.1}
          metalness={0.3}
          emissive="#93c5fd"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Left Eye */}
      <mesh ref={leftEyeRef} position={[-0.25, 0.15, 0.54]}>
        <sphereGeometry args={[0.08, 32, 32]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#60a5fa"
          emissiveIntensity={0.5}
          roughness={0.05}
        />
      </mesh>
      <mesh position={[-0.25, 0.15, 0.62]}>
        <sphereGeometry args={[0.04, 16, 16]} />
        <meshStandardMaterial color="#1e293b" roughness={0.1} />
      </mesh>

      {/* Right Eye */}
      <mesh ref={rightEyeRef} position={[0.25, 0.15, 0.54]}>
        <sphereGeometry args={[0.08, 32, 32]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#60a5fa"
          emissiveIntensity={0.5}
          roughness={0.05}
        />
      </mesh>
      <mesh position={[0.25, 0.15, 0.62]}>
        <sphereGeometry args={[0.04, 16, 16]} />
        <meshStandardMaterial color="#1e293b" roughness={0.1} />
      </mesh>

      {/* Mouth */}
      <mesh ref={mouthRef} position={[0, -0.2, 0.54]} rotation={[0, 0, Math.PI]}>
        <torusGeometry args={[0.15, 0.03, 16, 32, Math.PI]} />
        <meshStandardMaterial
          color="#f472b6"
          emissive="#f472b6"
          emissiveIntensity={1.2}
          roughness={0.1}
        />
      </mesh>

      {/* Antenna */}
      <mesh position={[0, 0.65, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.2, 16]} />
        <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
      </mesh>
      <mesh position={[0, 0.8, 0]} scale={[1, 1, 0.7]}>
        <sphereGeometry args={[0.06, 32, 32]} />
        <meshStandardMaterial
          color="#f472b6"
          emissive="#f472b6"
          emissiveIntensity={2.5}
          roughness={0.05}
        />
      </mesh>
    </group>
  );
}

export const MobileAvatar: React.FC<MobileAvatarProps> = ({ isSpeaking }) => {
  return (
    <div className="mobile-avatar-container">
      {/* Glow effect when speaking */}
      <AnimatePresence>
        {isSpeaking && (
          <motion.div
            className="mobile-avatar-glow"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      {/* 3D Canvas */}
      <Canvas
        shadows
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance',
        }}
      >
        <PerspectiveCamera makeDefault position={[0, 0, 3.5]} fov={35} />
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          minPolarAngle={Math.PI / 2}
          maxPolarAngle={Math.PI / 2}
          autoRotate={!isSpeaking}
          autoRotateSpeed={0.5}
        />

        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={0.8} castShadow />
        <pointLight position={[-5, 3, -5]} intensity={0.4} color="#60a5fa" />
        <pointLight position={[0, -2, 2]} intensity={0.3} color="#f472b6" />

        <RobotHead isSpeaking={isSpeaking} />
      </Canvas>

      {/* Speaking indicator */}
      {/* {isSpeaking && (
        <motion.div
          className="mobile-speaking-indicator"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          <motion.div
            className="mobile-sound-wave"
            animate={{ scaleY: [1, 1.5, 0.8, 1.3, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="mobile-sound-wave"
            animate={{ scaleY: [1, 0.8, 1.5, 1, 1.2] }}
            transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut', delay: 0.1 }}
          />
          <motion.div
            className="mobile-sound-wave"
            animate={{ scaleY: [1, 1.3, 1, 1.5, 0.9] }}
            transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
          />
        </motion.div>
      )} */}
    </div>
  );
};
